/**
 * Craftara editor tool registry — architecture target aligned with
 * Photoshop (precision tools) + Canva (content libraries).
 *
 * Implementation status is tracked per tool for roadmap planning.
 */

export type ToolPhase = 'mvp' | 'phase2' | 'phase3' | 'later';
export type ToolStatus = 'shipped' | 'partial' | 'planned';
export type ToolFamily =
  | 'selection'
  | 'drawing'
  | 'shape'
  | 'type'
  | 'retouch'
  | 'transform'
  | 'navigation'
  | 'measure'
  | 'library'
  | 'export';

export type ToolId =
  // Selection / transform
  | 'move'
  | 'marquee-rect'
  | 'marquee-ellipse'
  | 'lasso'
  | 'magic-wand'
  | 'crop'
  | 'slice'
  // Drawing
  | 'brush'
  | 'pencil'
  | 'eraser'
  | 'paint-bucket'
  | 'gradient'
  | 'pen'
  | 'pen-curvature'
  | 'shape-builder'
  // Shapes
  | 'shape-rect'
  | 'shape-rounded'
  | 'shape-ellipse'
  | 'shape-triangle'
  | 'shape-polygon'
  | 'shape-star'
  | 'shape-line'
  | 'shape-arrow'
  | 'frame'
  // Type
  | 'type-point'
  | 'type-area'
  | 'type-path'
  // Retouch / adjust
  | 'eyedropper'
  | 'hand'
  | 'zoom'
  | 'rotate-view'
  | 'ruler'
  | 'guides'
  | 'blur'
  | 'sharpen'
  | 'dodge'
  | 'burn'
  | 'clone-stamp'
  | 'spot-heal'
  | 'remove-bg'
  // Libraries (Canva-style panels, not brush tools)
  | 'lib-templates'
  | 'lib-elements'
  | 'lib-text-styles'
  | 'lib-uploads'
  | 'lib-photos'
  | 'lib-icons'
  | 'lib-charts'
  | 'lib-brand'
  | 'lib-apps'
  // Object ops
  | 'align'
  | 'distribute'
  | 'group'
  | 'ungroup'
  | 'boolean-union'
  | 'boolean-subtract'
  | 'mask'
  | 'clip'
  // Export / view
  | 'export'
  | 'present'
  | 'history'
  | 'layers'
  | 'properties'
  | 'comments';

export interface EditorToolDef {
  id: ToolId;
  /** UI label */
  label: string;
  family: ToolFamily;
  /** Photoshop analogue (if any) */
  photoshop?: string;
  /** Canva analogue (if any) */
  canva?: string;
  /** Keyboard shortcut target */
  shortcut?: string;
  phase: ToolPhase;
  status: ToolStatus;
  /** Fabric.js / app implementation notes */
  implNotes: string;
}

/**
 * Canonical tool inventory for Craftara Studio.
 * Keep in sync with ToolStrip / DesignPanel as tools ship.
 */
export const EDITOR_TOOL_REGISTRY: EditorToolDef[] = [
  // —— Selection ——
  {
    id: 'move',
    label: 'Move / Select',
    family: 'selection',
    photoshop: 'Move (V)',
    canva: 'Select',
    shortcut: 'V',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'Fabric selection + transform controls',
  },
  {
    id: 'marquee-rect',
    label: 'Rectangular marquee',
    family: 'selection',
    photoshop: 'Rectangular Marquee (M)',
    canva: '—',
    shortcut: 'M',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Drag rect to multi-select intersecting objects; switches to Move',
  },
  {
    id: 'marquee-ellipse',
    label: 'Elliptical marquee',
    family: 'selection',
    photoshop: 'Elliptical Marquee',
    canva: '—',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Options bar Ellipse mode · M cycles rect/ellipse',
  },
  {
    id: 'lasso',
    label: 'Lasso select',
    family: 'selection',
    photoshop: 'Lasso (L)',
    canva: '—',
    shortcut: 'L',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Freehand object select (centers inside polygon); Shift adds',
  },
  {
    id: 'magic-wand',
    label: 'Magic wand',
    family: 'selection',
    photoshop: 'Magic Wand (W)',
    canva: 'Background remover (related)',
    phase: 'later',
    status: 'planned',
    implNotes: 'Requires pixel buffer / image analysis',
  },
  {
    id: 'crop',
    label: 'Crop',
    family: 'transform',
    photoshop: 'Crop (C)',
    canva: 'Crop',
    shortcut: 'C',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Image overlay crop (cropX/Y) or drag artboard crop; Enter apply / Esc cancel',
  },

  // —— Drawing ——
  {
    id: 'brush',
    label: 'Brush',
    family: 'drawing',
    photoshop: 'Brush (B)',
    canva: 'Draw',
    shortcut: 'B',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'Fabric freeDrawingBrush',
  },
  {
    id: 'pencil',
    label: 'Pencil',
    family: 'drawing',
    photoshop: 'Pencil',
    canva: 'Draw (hard)',
    shortcut: 'N',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'PencilBrush, hard edges, higher decimate than Brush',
  },
  {
    id: 'eraser',
    label: 'Eraser',
    family: 'drawing',
    photoshop: 'Eraser (E)',
    canva: 'Eraser',
    shortcut: 'E',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Drag to remove objects under cursor',
  },
  {
    id: 'paint-bucket',
    label: 'Paint bucket',
    family: 'drawing',
    photoshop: 'Paint Bucket (G)',
    canva: '—',
    shortcut: 'K',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Click shape to fill / empty artboard for background',
  },
  {
    id: 'gradient',
    label: 'Gradient',
    family: 'drawing',
    photoshop: 'Gradient (G)',
    canva: 'Gradient fill',
    shortcut: 'G',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Apply linear/radial fill via tool + Properties',
  },
  {
    id: 'pen',
    label: 'Pen / Path',
    family: 'drawing',
    photoshop: 'Pen (P)',
    canva: '— (limited)',
    shortcut: 'P',
    phase: 'phase3',
    status: 'shipped',
    implNotes: 'Click points, Enter/double-click to finish fabric.Path',
  },

  // —— Shapes ——
  {
    id: 'shape-rect',
    label: 'Rectangle',
    family: 'shape',
    photoshop: 'Rectangle (U)',
    canva: 'Elements → Shapes',
    shortcut: 'U',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'Drag-to-create fabric.Rect; optional stroke + corner radius',
  },
  {
    id: 'shape-rounded',
    label: 'Rounded rectangle',
    family: 'shape',
    photoshop: 'Rounded Rectangle',
    canva: 'Elements → Shapes',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Drag-to-create Rect with rx/ry 16',
  },
  {
    id: 'shape-ellipse',
    label: 'Ellipse',
    family: 'shape',
    photoshop: 'Ellipse (U)',
    canva: 'Elements → Shapes',
    shortcut: 'O',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'Drag-to-create fabric.Ellipse',
  },
  {
    id: 'shape-triangle',
    label: 'Triangle',
    family: 'shape',
    photoshop: 'Polygon/Triangle',
    canva: 'Elements → Shapes',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'Drag-to-create + Elements panel',
  },
  {
    id: 'shape-polygon',
    label: 'Polygon',
    family: 'shape',
    photoshop: 'Polygon',
    canva: 'Elements → Shapes',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Drag-to-create N-gon; Options bar sides (3–12)',
  },
  {
    id: 'shape-star',
    label: 'Star',
    family: 'shape',
    photoshop: 'Star',
    canva: 'Elements → Shapes',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'Drag-to-create polygon star',
  },
  {
    id: 'shape-line',
    label: 'Line',
    family: 'shape',
    photoshop: 'Line',
    canva: 'Elements → Lines',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'Drag-to-create fabric.Line',
  },
  {
    id: 'shape-arrow',
    label: 'Arrow',
    family: 'shape',
    photoshop: 'Line + arrowheads',
    canva: 'Elements → Lines/Arrows',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Drag line, finalize as line + head group',
  },
  {
    id: 'frame',
    label: 'Frame / Mask',
    family: 'shape',
    photoshop: 'Frame Tool / Clipping Mask',
    canva: 'Frames',
    shortcut: 'F',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Drag frame + clipPath image fill / Create mask',
  },

  // —— Type ——
  {
    id: 'type-point',
    label: 'Type (point)',
    family: 'type',
    photoshop: 'Horizontal Type (T)',
    canva: 'Text',
    shortcut: 'T',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'fabric.IText click-to-place',
  },
  {
    id: 'type-area',
    label: 'Type (area)',
    family: 'type',
    photoshop: 'Type in paragraph box',
    canva: 'Text box',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'fabric.Textbox drag-to-create with wrapping',
  },
  {
    id: 'type-path',
    label: 'Type on path',
    family: 'type',
    photoshop: 'Type on a Path',
    canva: 'Curved text (apps)',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Properties: place characters along selected path/line/shape edge',
  },

  // —— Navigation / measure ——
  {
    id: 'hand',
    label: 'Hand / Pan',
    family: 'navigation',
    photoshop: 'Hand (H)',
    canva: 'Pan canvas',
    shortcut: 'H',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Space+drag or Hand tool pans stage scroll',
  },
  {
    id: 'zoom',
    label: 'Zoom',
    family: 'navigation',
    photoshop: 'Zoom (Z)',
    canva: 'Zoom controls',
    shortcut: 'Z',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'Click zoom in, Alt+click out; status bar + Ctrl+wheel',
  },
  {
    id: 'eyedropper',
    label: 'Eyedropper',
    family: 'retouch',
    photoshop: 'Eyedropper (I)',
    canva: 'Color picker from design',
    shortcut: 'I',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Click object to sample fill/stroke into active color',
  },
  {
    id: 'ruler',
    label: 'Rulers',
    family: 'measure',
    photoshop: 'Rulers (Ctrl+R)',
    canva: '—',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Pixel rulers around stage; Ctrl+R toggle',
  },
  {
    id: 'guides',
    label: 'Guides / Snap',
    family: 'measure',
    photoshop: 'Guides / Smart Guides',
    canva: 'Snap / margins',
    phase: 'phase2',
    status: 'shipped',
    implNotes:
      'Smart snap + ruler-drag guides + optional 8px grid snap (status bar Grid)',
  },

  // —— Retouch / AI ——
  {
    id: 'remove-bg',
    label: 'Remove background',
    family: 'retouch',
    photoshop: 'Select Subject / Remove bg',
    canva: 'BG Remover',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Properties Remove BG; cloud callable + local fallback',
  },
  {
    id: 'blur',
    label: 'Blur filter',
    family: 'retouch',
    photoshop: 'Blur tool / Filter',
    canva: 'Effects → Blur',
    phase: 'phase2',
    status: 'shipped',
    implNotes:
      'Properties Adjustments: Brightness / Contrast / Blur / Saturation on images',
  },
  {
    id: 'spot-heal',
    label: 'Spot heal',
    family: 'retouch',
    photoshop: 'Spot Healing Brush',
    canva: 'Magic Edit (limited)',
    phase: 'later',
    status: 'planned',
    implNotes: 'Raster-only; defer',
  },

  // —— Libraries (Canva panels) ——
  {
    id: 'lib-templates',
    label: 'Templates',
    family: 'library',
    photoshop: '—',
    canva: 'Templates',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'DesignPanel + local catalog',
  },
  {
    id: 'lib-elements',
    label: 'Elements',
    family: 'library',
    photoshop: 'Shapes panel',
    canva: 'Elements',
    phase: 'mvp',
    status: 'shipped',
    implNotes:
      'Shapes/frames + Photos/Icons; CorelDRAW-style Flowchart/UML/Arrows via diagram factory',
  },
  {
    id: 'lib-text-styles',
    label: 'Text styles',
    family: 'library',
    photoshop: 'Character Styles',
    canva: 'Text',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'Heading/body/quote presets',
  },
  {
    id: 'lib-uploads',
    label: 'Uploads',
    family: 'library',
    photoshop: 'Place Embedded',
    canva: 'Uploads',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'Firebase Storage under projects/.../uploads; local dataURL fallback',
  },
  {
    id: 'lib-photos',
    label: 'Photos / Stock',
    family: 'library',
    photoshop: 'Libraries / Stock',
    canva: 'Photos',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Local SVG stock pack in DesignPanel Photos tab',
  },
  {
    id: 'lib-icons',
    label: 'Icons',
    family: 'library',
    photoshop: '—',
    canva: 'Graphics / Icons',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Local SVG line icons in DesignPanel Icons tab',
  },
  {
    id: 'lib-charts',
    label: 'Charts',
    family: 'library',
    photoshop: '—',
    canva: 'Charts',
    phase: 'phase3',
    status: 'shipped',
    implNotes: 'SVG bar/pie/line presets in DesignPanel',
  },
  {
    id: 'lib-brand',
    label: 'Brand kit',
    family: 'library',
    photoshop: 'CC Libraries',
    canva: 'Brand Kit',
    phase: 'phase3',
    status: 'shipped',
    implNotes: 'Local brand colors/fonts/logo in DesignPanel',
  },

  // —— Object ops ——
  {
    id: 'align',
    label: 'Align',
    family: 'transform',
    photoshop: 'Align panel',
    canva: 'Position → Align',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Properties panel: align to artboard/selection',
  },
  {
    id: 'distribute',
    label: 'Distribute',
    family: 'transform',
    photoshop: 'Distribute',
    canva: 'Position → Space evenly',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Properties: horizontal/vertical gap distribute for 2+ selection',
  },
  {
    id: 'group',
    label: 'Group',
    family: 'transform',
    photoshop: 'Group Layers',
    canva: 'Group',
    shortcut: 'Ctrl+G',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Ctrl+G / Ctrl+Shift+G + Properties panel',
  },
  {
    id: 'boolean-union',
    label: 'Boolean combine',
    family: 'transform',
    photoshop: 'Shape Builder / Pathfinder',
    canva: '—',
    phase: 'phase2',
    status: 'shipped',
    implNotes:
      'Properties Pathfinder: Union / Subtract / Intersect / Exclude → Polygon outline',
  },
  {
    id: 'boolean-subtract',
    label: 'Boolean subtract',
    family: 'transform',
    photoshop: 'Subtract / Minus Front',
    canva: '—',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Pathfinder Subtract — cuts upper shapes from back-most',
  },
  {
    id: 'mask',
    label: 'Clipping mask',
    family: 'transform',
    photoshop: 'Create Clipping Mask',
    canva: 'Frames / Mask',
    phase: 'phase2',
    status: 'shipped',
    implNotes: 'Select image+shape → Properties Create mask',
  },

  // —— Panels / export ——
  {
    id: 'layers',
    label: 'Layers panel',
    family: 'export',
    photoshop: 'Layers',
    canva: 'Position / layers',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'Select/hide/lock/rename/duplicate/reorder; locks persist in JSON',
  },
  {
    id: 'properties',
    label: 'Properties panel',
    family: 'export',
    photoshop: 'Properties',
    canva: 'Edit panel',
    phase: 'mvp',
    status: 'shipped',
    implNotes:
      'Position, fill/stroke, font, blend, flip/rotate, pathfinder, type, arrange',
  },
  {
    id: 'history',
    label: 'History',
    family: 'export',
    photoshop: 'History',
    canva: 'Undo stack',
    phase: 'mvp',
    status: 'shipped',
    implNotes: 'Right dock History panel with jump-to; undo/redo reloads Fabric',
  },
  {
    id: 'export',
    label: 'Export',
    family: 'export',
    photoshop: 'Export As',
    canva: 'Download',
    shortcut: 'Ctrl+E',
    phase: 'mvp',
    status: 'shipped',
    implNotes:
      'PNG/JPG/SVG/PDF; all-pages ZIP or multi-page PDF; free quota 5/mo, Pro unlimited',
  },
  {
    id: 'present',
    label: 'Present',
    family: 'export',
    photoshop: '—',
    canva: 'Present',
    shortcut: 'F5',
    phase: 'phase3',
    status: 'shipped',
    implNotes: 'Fullscreen slideshow of all project pages (F5)',
  },
  {
    id: 'comments',
    label: 'Comments',
    family: 'export',
    photoshop: '—',
    canva: 'Comments',
    phase: 'phase3',
    status: 'shipped',
    implNotes: 'Pin comments on artboard; Firestore + local fallback',
  },
];

/**
 * Left tool-strip order (Photoshop-style primary tools).
 * `shape-rect` is the flyout anchor for all shape tools.
 * Planned tools still render as disabled placeholders.
 */
export const TOOL_STRIP_IDS: ToolId[] = [
  'move',
  'marquee-rect',
  'lasso',
  'brush',
  'pencil',
  'eraser',
  'pen',
  'shape-rect',
  'type-point',
  'frame',
  'gradient',
  'paint-bucket',
  'eyedropper',
  'crop',
  'hand',
  'zoom',
];

/** Shape tools shown in the U-key flyout (order = cycle order). */
export const SHAPE_FLYOUT_IDS: ToolId[] = [
  'shape-rect',
  'shape-rounded',
  'shape-ellipse',
  'shape-triangle',
  'shape-polygon',
  'shape-star',
  'shape-line',
  'shape-arrow',
];

/** Type tools shown in the T-key flyout. */
export const TYPE_FLYOUT_IDS: ToolId[] = ['type-point', 'type-area'];

/**
 * Runtime tool keys used by the web editor (Fabric interaction modes).
 * Maps 1:1 with Zustand `selectedTool`.
 */
export type RuntimeCanvasTool =
  | 'select'
  | 'marquee'
  | 'lasso'
  | 'draw'
  | 'pencil'
  | 'pen'
  | 'rectangle'
  | 'roundedRect'
  | 'circle'
  | 'triangle'
  | 'polygon'
  | 'star'
  | 'line'
  | 'arrow'
  | 'text'
  | 'textbox'
  | 'frame'
  | 'gradient'
  | 'bucket'
  | 'eyedropper'
  | 'crop'
  | 'hand'
  | 'zoom'
  | 'eraser'
  | 'comment';

export type ShapeRuntimeTool = Extract<
  RuntimeCanvasTool,
  | 'rectangle'
  | 'roundedRect'
  | 'circle'
  | 'triangle'
  | 'polygon'
  | 'star'
  | 'line'
  | 'arrow'
>;

export type MarqueeShape = 'rect' | 'ellipse';

export type TypeRuntimeTool = Extract<RuntimeCanvasTool, 'text' | 'textbox'>;

export const TOOL_ID_TO_RUNTIME: Partial<Record<ToolId, RuntimeCanvasTool>> = {
  move: 'select',
  'marquee-rect': 'marquee',
  lasso: 'lasso',
  brush: 'draw',
  pencil: 'pencil',
  pen: 'pen',
  'shape-rect': 'rectangle',
  'shape-rounded': 'roundedRect',
  'shape-ellipse': 'circle',
  'shape-triangle': 'triangle',
  'shape-polygon': 'polygon',
  'shape-star': 'star',
  'shape-line': 'line',
  'shape-arrow': 'arrow',
  'type-point': 'text',
  'type-area': 'textbox',
  frame: 'frame',
  gradient: 'gradient',
  'paint-bucket': 'bucket',
  eyedropper: 'eyedropper',
  crop: 'crop',
  hand: 'hand',
  zoom: 'zoom',
  eraser: 'eraser',
  comments: 'comment',
};

export const RUNTIME_TO_TOOL_ID: Record<RuntimeCanvasTool, ToolId> = {
  select: 'move',
  marquee: 'marquee-rect',
  lasso: 'lasso',
  draw: 'brush',
  pencil: 'pencil',
  pen: 'pen',
  rectangle: 'shape-rect',
  roundedRect: 'shape-rounded',
  circle: 'shape-ellipse',
  triangle: 'shape-triangle',
  polygon: 'shape-polygon',
  star: 'shape-star',
  line: 'shape-line',
  arrow: 'shape-arrow',
  text: 'type-point',
  textbox: 'type-area',
  frame: 'frame',
  gradient: 'gradient',
  bucket: 'paint-bucket',
  eyedropper: 'eyedropper',
  crop: 'crop',
  hand: 'hand',
  zoom: 'zoom',
  eraser: 'eraser',
  comment: 'comments',
};

export function getToolById(id: ToolId): EditorToolDef | undefined {
  return EDITOR_TOOL_REGISTRY.find((t) => t.id === id);
}

export function getToolStripDefs(): EditorToolDef[] {
  return TOOL_STRIP_IDS.map((id) => getToolById(id)).filter(
    (t): t is EditorToolDef => Boolean(t)
  );
}

export function getShapeFlyoutDefs(): EditorToolDef[] {
  return SHAPE_FLYOUT_IDS.map((id) => getToolById(id)).filter(
    (t): t is EditorToolDef => Boolean(t)
  );
}

export function getTypeFlyoutDefs(): EditorToolDef[] {
  return TYPE_FLYOUT_IDS.map((id) => getToolById(id)).filter(
    (t): t is EditorToolDef => Boolean(t)
  );
}

export function isShapeRuntimeTool(tool: RuntimeCanvasTool): tool is ShapeRuntimeTool {
  return (
    tool === 'rectangle' ||
    tool === 'roundedRect' ||
    tool === 'circle' ||
    tool === 'triangle' ||
    tool === 'polygon' ||
    tool === 'star' ||
    tool === 'line' ||
    tool === 'arrow'
  );
}

export function isTypeRuntimeTool(tool: RuntimeCanvasTool): tool is TypeRuntimeTool {
  return tool === 'text' || tool === 'textbox';
}

/** Only shipped tools with a Fabric runtime mapping are selectable in the strip. */
export function canActivateTool(def: EditorToolDef): boolean {
  return def.status === 'shipped' && Boolean(TOOL_ID_TO_RUNTIME[def.id]);
}

export function toolsByPhase(phase: ToolPhase): EditorToolDef[] {
  return EDITOR_TOOL_REGISTRY.filter((t) => t.phase === phase);
}

export function toolsByStatus(status: ToolStatus): EditorToolDef[] {
  return EDITOR_TOOL_REGISTRY.filter((t) => t.status === status);
}

export function toolsByFamily(family: ToolFamily): EditorToolDef[] {
  return EDITOR_TOOL_REGISTRY.filter((t) => t.family === family);
}
